import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, Globe2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { normalizeUiLocale, SUPPORTED_LOCALES } from '../utils/locale';

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const menuId = useId();
  const currentLocale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const currentOption = SUPPORTED_LOCALES.find(({ code }) => code === currentLocale);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (open) {
      const currentIndex = SUPPORTED_LOCALES.findIndex(({ code }) => code === currentLocale);
      itemRefs.current[currentIndex]?.focus();
    }
  }, [currentLocale, open]);

  const selectLanguage = (locale) => {
    i18n.changeLanguage(locale);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleButtonKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    setOpen(true);
  };

  const handleMenuKeyDown = (event) => {
    const activeIndex = itemRefs.current.findIndex((item) => item === document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const targetIndex = event.key === 'Home' ? 0 : SUPPORTED_LOCALES.length - 1;
      itemRefs.current[targetIndex]?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (activeIndex + offset + SUPPORTED_LOCALES.length) % SUPPORTED_LOCALES.length;
    itemRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="language-switcher" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={handleButtonKeyDown}
        className="lang-switch"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('common.selectLanguage')}
        title={t('common.selectLanguage')}
      >
        <Globe2 size={16} strokeWidth={2} aria-hidden="true" />
        <span className="lang-switch__label" dir="auto">{currentOption?.nativeName}</span>
        <ChevronDown className="lang-switch__chevron" size={13} strokeWidth={2} aria-hidden="true" />
      </button>

      {open && (
        <div id={menuId} className="dropdown-panel language-menu" role="listbox" aria-label={t('common.selectLanguage')} onKeyDown={handleMenuKeyDown}>
          {SUPPORTED_LOCALES.map(({ code, nativeName }, index) => (
            <button
              key={code}
              ref={(element) => { itemRefs.current[index] = element; }}
              type="button"
              role="option"
              aria-selected={code === currentLocale}
              className={`dropdown-panel__item language-menu__item${code === currentLocale ? ' is-active' : ''}`}
              onClick={() => selectLanguage(code)}
            >
              <span className="language-menu__name" dir="auto">{nativeName}</span>
              <span className="language-menu__code" dir="ltr">{code}</span>
              <span className="language-menu__check">{code === currentLocale && <Check size={14} strokeWidth={2.5} aria-hidden="true" />}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
